import { useEffect, useState } from "react";
import { create } from "zustand";
import { audioContext } from "./volume";
import { heldRoom } from "./room";

/**
 * Выбор устройств: микрофон, наушники, камера.
 *
 * Браузер отдаёт список только после разрешения — до него у устройств пустые
 * имена, и выбирать не из чего. Поэтому окно настроек само берёт микрофон,
 * когда его открывают: не ради красоты живой полоски, а чтобы список вообще
 * стал списком.
 *
 * Пропажу устройства держим отдельно от списка. Исчезнувшая строка — худший
 * способ сообщить, что гарнитуру выдернули: человек видит другое имя в поле
 * и думает, что сам его выбрал. Поэтому пропавшее остаётся видимым вместе с
 * тем, когда это случилось.
 */

const KEY = "airel.devices";

export type DeviceKind = "microphone" | "speaker" | "camera";

export interface DeviceChoice {
  microphone: string | null;
  speaker: string | null;
  camera: string | null;
}

const NONE: DeviceChoice = { microphone: null, speaker: null, camera: null };

function read(): DeviceChoice {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? { ...NONE, ...(JSON.parse(raw) as Partial<DeviceChoice>) } : { ...NONE };
  } catch {
    return { ...NONE };
  }
}

/** Стором zustand, а не своим списком подписчиков: см. `volume.ts`. */
const useChoice = create<DeviceChoice>()(() => read());

export function deviceChoice(): DeviceChoice {
  return useChoice.getState();
}

export function chooseDevice(kind: DeviceKind, id: string | null): void {
  useChoice.setState({ [kind]: id });

  try {
    localStorage.setItem(KEY, JSON.stringify(useChoice.getState()));
  } catch {
    // Приватный режим. Выбор доживёт до перезагрузки в памяти.
  }

  // Наушники переключаются сразу: звук собеседников идёт через свой контекст,
  // и ждать следующего звонка тут нечего.
  if (kind === "speaker") {
    void applySpeaker(id);
    return;
  }

  // Микрофон и камеру переводим на живой дорожке идущего звонка. Иначе выбор
  // доезжал бы только `audioCaptureDefaults`/`videoCaptureDefaults` при входе
  // в канал — то самое «работает только при переподключении».
  void applyLiveDevice(kind, id);
}

/**
 * Переводит живую дорожку звонка на выбранное устройство.
 *
 * LiveKit сам перезапускает дорожку с новым устройством, а наш тракт шумодава
 * переживает перезапуск (`setDeviceId` зовёт `restart` у поставленного
 * обработчика). Вне звонка комнаты нет — и это не беда: выбор сохранён в
 * сторе, и вошедший в канал возьмёт его через `deviceOf` при создании комнаты.
 */
async function applyLiveDevice(kind: "microphone" | "camera", id: string | null): Promise<void> {
  const room = heldRoom();
  if (!room) return;

  const mediaKind = kind === "microphone" ? "audioinput" : "videoinput";

  try {
    /*
     * Третий довод — точность совпадения, и для конкретного устройства она
     * обязана быть `true`.
     *
     * При `false` deviceId уходит как «желаемый» (ideal). Обычный Chrome в
     * вебе его чтит, а Chromium внутри Electron нередко пропускает мимо и
     * оставляет прежний микрофон — отсюда «в вебе переключается, в приложении
     * всегда первый выбранный». `{ exact: … }` не оставляет выбора: либо то
     * устройство, либо осознанный отказ (его ловит `catch`).
     *
     * Исключение — системное по умолчанию (`id === null` → «default»): это
     * псевдоним, а не настоящее устройство, и требовать его точно незачем.
     */
    await room.switchActiveDevice(mediaKind, id ?? "default", id !== null);
  } catch {
    // Устройство могли выдернуть между выбором и переключением. Выбор всё
    // равно сохранён, следующий вход в канал возьмёт его штатно.
  }
}

/**
 * Переводит вывод на выбранное устройство.
 *
 * `setSinkId` у звукового контекста есть не везде: в Chrome и WebView2 есть,
 * в Firefox за флагом, в Safari нет вовсе. Где нет — молча остаёмся на
 * системном устройстве, а строка выбора об этом говорит.
 */
export async function applySpeaker(id: string | null): Promise<boolean> {
  const context = audioContext() as AudioContext & {
    setSinkId?: (id: string) => Promise<void>;
  };

  if (typeof context.setSinkId !== "function") return false;

  try {
    await context.setSinkId(id ?? "");
    return true;
  } catch {
    return false;
  }
}

/** Умеет ли эта сборка браузера переключать вывод. */
export function speakerSwitchable(): boolean {
  return typeof (audioContext() as { setSinkId?: unknown }).setSinkId === "function";
}

export interface Device {
  id: string;
  label: string;
}

export interface Devices {
  microphones: Device[];
  speakers: Device[];
  cameras: Device[];
  /** Есть ли у списков настоящие имена — то есть выдано ли разрешение. */
  named: boolean;
}

const EMPTY: Devices = { microphones: [], speakers: [], cameras: [], named: false };

function label(device: MediaDeviceInfo, index: number): string {
  if (device.label) return device.label;

  // Без разрешения имён нет вовсе. Показывать пустую строку нельзя, а
  // «Устройство 2» хотя бы отличает одно от другого.
  const kind =
    device.kind === "audioinput" ? "Микрофон" : device.kind === "audiooutput" ? "Выход" : "Камера";
  return `${kind} ${index + 1}`;
}

export async function listDevices(): Promise<Devices> {
  if (typeof navigator === "undefined" || !navigator.mediaDevices?.enumerateDevices) return EMPTY;

  const all = await navigator.mediaDevices.enumerateDevices();
  const of = (kind: MediaDeviceKind) =>
    all
      .filter((device) => device.kind === kind)
      // «default» и «communications» — это не устройства, а псевдонимы
      // системного выбора. Показывать их наравне с настоящими значит
      // предлагать выбрать одно и то же дважды.
      .filter((device) => device.deviceId !== "communications")
      .map((device, index) => ({ id: device.deviceId, label: label(device, index) }));

  return {
    microphones: of("audioinput"),
    speakers: of("audiooutput"),
    cameras: of("videoinput"),
    named: all.some((device) => !!device.label),
  };
}

/** Что случилось с выбранным устройством, если его больше нет. */
export interface Lost {
  kind: DeviceKind;
  /** Имя из системы; `null` — имени не узнали. */
  label: string | null;
  /** Когда пропало. Нужно, чтобы сказать «отключилась 2 минуты назад». */
  at: number;
}

/**
 * Имена устройств и время пропажи — общие на все `useDevices`, а не у каждого
 * свои.
 *
 * Окно настроек заводит свой крючок при открытии, и своя память у него
 * начиналась пустой: устройство, пропавшее до открытия окна, оно не знало по
 * имени («Прежнее устройство») и считало пропавшим «только что».
 */
const labels = new Map<string, string>();
const goneAt = new Map<string, number>();

/**
 * Списки, выбор и пропажи — одним крючком для экрана настроек.
 *
 * Список перечитывается по событию `devicechange`, а не по таймеру: браузер
 * сам говорит, когда что-то воткнули или выдернули.
 */
export function useDevices(): {
  devices: Devices;
  choice: DeviceChoice;
  lost: Lost[];
} {
  const [devices, setDevices] = useState<Devices>(EMPTY);
  const choice = useChoice();
  const [lost, setLost] = useState<Lost[]>([]);

  useEffect(() => {
    let alive = true;

    const refresh = async () => {
      const next = await listDevices();
      if (!alive) return;

      // Пустое имя — разрешения ещё не дали: такое не запоминаем.
      for (const list of [next.microphones, next.speakers, next.cameras]) {
        for (const device of list) if (device.label) labels.set(device.id, device.label);
      }

      const kinds: [DeviceKind, Device[]][] = [
        ["microphone", next.microphones],
        ["speaker", next.speakers],
        ["camera", next.cameras],
      ];

      // Из стора, а не из отрисовки: подписка живёт с пустыми зависимостями,
      // и снятое при первой отрисовке значение к третьему перечитыванию
      // списка успело бы устареть.
      const chosenNow = useChoice.getState();
      const gone: Lost[] = [];

      for (const [kind, list] of kinds) {
        const chosen = chosenNow[kind];
        if (!chosen) continue;

        const key = `${kind}:${chosen}`;
        if (list.some((device) => device.id === chosen)) {
          goneAt.delete(key);
          continue;
        }

        // Время пропажи — первое, когда её заметили: иначе «2 минуты назад»
        // обнулялось бы на каждом перечитывании списка.
        if (!goneAt.has(key)) goneAt.set(key, Date.now());
        gone.push({ kind, label: labels.get(chosen) ?? null, at: goneAt.get(key)! });
      }

      setLost(gone);

      setDevices(next);
    };

    void refresh();

    const media = navigator.mediaDevices;
    media?.addEventListener?.("devicechange", refresh);

    return () => {
      alive = false;
      media?.removeEventListener?.("devicechange", refresh);
    };
  }, []);

  return { devices, choice, lost };
}

/** Ограничение выбора для `getUserMedia`: пусто — системное устройство. */
export function deviceOf(kind: DeviceKind): { deviceId?: string } {
  const id = useChoice.getState()[kind];
  return id ? { deviceId: id } : {};
}
